import { XYZ_DEX_NAME } from "#/features/trade/lib/hyperliquid";
import {
	useDeposit,
	usePlaceOrder,
	useUpdateLeverage,
} from "#/hooks/use-trading-actions";
import { useTradingBalance } from "#/hooks/use-trading-balance";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import { useWallet } from "#/hooks/use-wallet";
import { toNumber } from "#/lib/formatters";
import {
	useTradeAccount,
	useTradeActiveAssetData,
	useTradeMarket,
	useTradeOrderBook,
} from "../../../hooks/use-trade-data.ts";
import { useTradeSelectedPrice } from "../../trade-selected-price-context.tsx";
import { getMarginCapability } from "../utils/margin-mode.ts";
import { computeCrossEquity } from "./cross-equity.ts";
import { useOrderFormState } from "./use-order-form-state.ts";

export function useOrderFormSetup() {
	const wallet = useWallet();
	const { marketData } = useTradeMarket();
	const { book } = useTradeOrderBook();
	const { accountState, positions } = useTradeAccount();
	const { activeAssetData } = useTradeActiveAssetData();
	const { selectedPrice } = useTradeSelectedPrice();
	const {
		spotUsdc: rawSpotUsdc,
		spotUsdcHold,
		isUnified,
		isDexAbstraction,
	} = useUnifiedBalance();
	const tradingBalance = useTradingBalance();
	const updateLeverage = useUpdateLeverage();
	const depositAction = useDeposit();
	const placeOrder = usePlaceOrder();
	const currentPosition = positions.find((p) => p.coin === marketData.coin);
	const bestBid = book?.levels[0]?.[0]?.px;
	const bestAsk = book?.levels[1]?.[0]?.px;

	const marginCapability = getMarginCapability({
		coinMarginMode: marketData.marginMode,
	});
	// Spendable equity — drives the "has funds?" gate. Shared with
	// useNeedsDepositToTrading so both answer that question the same way.
	const effectiveAccountValue = String(tradingBalance);
	const effectiveWithdrawable = String(toNumber(accountState?.withdrawable));
	// Equity backing a Cross position — what the liquidation estimate consumes.
	// Not the trading balance above: that one is "what can be spent", this one is
	// "what absorbs an adverse move". See computeCrossEquity for why they differ.
	const crossEquity = String(
		computeCrossEquity({
			crossMarginAccountValue: accountState?.crossMarginSummary?.accountValue,
			marginAccountValue: accountState?.marginSummary?.accountValue,
			spotUsdc: rawSpotUsdc,
			spotUsdcHold,
			isUnified,
		}),
	);

	const form = useOrderFormState({
		accountAddress: wallet.address,
		coin: marketData.coin,
		markPrice: marketData.markPrice,
		maxLeverage: marketData.maxLeverage,
		szDecimals: marketData.szDecimals ?? 4,
		withdrawable: effectiveWithdrawable,
		availableToTrade: activeAssetData?.availableToTrade,
		maxTradeSzs: activeAssetData?.maxTradeSzs,
		isXyzMarket: marketData.dex === XYZ_DEX_NAME,
		isUnified,
		isDexAbstraction,
		accountValue: effectiveAccountValue,
		perpAccountValue: crossEquity,
		currentPositionSize: currentPosition?.szi,
		currentPositionEntryPx: currentPosition?.entryPx,
		currentPositionLiquidationPx: currentPosition?.liquidationPx,
		currentPositionLeverage:
			activeAssetData?.leverage ?? currentPosition?.leverage,
		selectedPrice,
		bestBid,
		bestAsk,
		isCrossMarginSupported: marginCapability.supportsCrossMargin,
		defaultMarginMode: marginCapability.defaultMarginMode,
	});

	const quickPrice = form.side === "buy" ? bestBid : bestAsk;

	return {
		wallet,
		marketData,
		form,
		quickPrice,
		marginCapability,
		updateLeverage,
		depositAction,
		placeOrder,
	};
}
